import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquare, Receipt as ReceiptIcon, Loader2, AlertTriangle, ExternalLink, FileText } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { Dialog, DialogContent } from "./ui/dialog";
import { API_BASE, TOKEN_KEY } from "../lib/api";

const COMMENT_TRUNCATE = 200;

// Module-level cache so repeated hovers across rows / pages never re-fetch.
// Maps receipt_id → { url: string, contentType: string } | { error, status }.
const RECEIPT_CACHE = new Map();
const RECEIPT_INFLIGHT = new Map();

async function fetchReceiptBlob(id) {
  const cached = RECEIPT_CACHE.get(id);
  if (cached) return cached;
  if (RECEIPT_INFLIGHT.has(id)) return RECEIPT_INFLIGHT.get(id);
  const promise = (async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const resp = await fetch(`${API_BASE}/receipts/${id}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!resp.ok) {
      const err = { error: true, status: resp.status };
      RECEIPT_CACHE.set(id, err);
      return err;
    }
    const blob = await resp.blob();
    const url = URL.createObjectURL(blob);
    const entry = { url, contentType: blob.type || "image/*" };
    RECEIPT_CACHE.set(id, entry);
    return entry;
  })();
  RECEIPT_INFLIGHT.set(id, promise);
  try {
    return await promise;
  } finally {
    RECEIPT_INFLIGHT.delete(id);
  }
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? "" : "s"} ago`;
  const yr = Math.round(mo / 12);
  return `${yr} year${yr === 1 ? "" : "s"} ago`;
}

function CommentPopover({ expense, size }) {
  const fullText = expense.comment || "";
  const truncated = fullText.length > COMMENT_TRUNCATE;
  const shown = truncated ? `${fullText.slice(0, COMMENT_TRUNCATE).trimEnd()}…` : fullText;
  const author = expense.comment_author_name || expense.user_name || expense.user_email || null;
  const when = formatRelative(expense.comment_at || expense.updated_at || expense.created_at);
  const iconCls = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          aria-label="Show comment"
          data-testid={`expense-row-comment-icon-${expense.id}`}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700">
          <MessageSquare className={iconCls} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="center"
        className="w-80 p-3"
        data-testid={`expense-row-comment-popover-${expense.id}`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-2">
          <MessageSquare className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" />
          <p className="whitespace-pre-wrap break-words text-sm leading-snug text-slate-800">
            {shown || <span className="italic text-slate-400">(no comment)</span>}
          </p>
        </div>
        {truncated && (
          <Link
            to={`/expenses/${expense.id}`}
            onClick={(e) => e.stopPropagation()}
            data-testid={`expense-row-comment-seefull-${expense.id}`}
            className="mt-2 inline-block text-xs font-medium text-[#0F1E3D] hover:underline">
            See full →
          </Link>
        )}
        {(author || when) && (
          <p className="mt-2 border-t border-slate-100 pt-1.5 text-xs text-slate-500">
            — {author || "unknown"}{when ? `, ${when}` : ""}
          </p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

function ReceiptThumb({ receiptId, expense, onOpenModal }) {
  const [state, setState] = useState(() => {
    const c = RECEIPT_CACHE.get(receiptId);
    if (!c) return { loading: false, started: false };
    if (c.error) return { loading: false, error: true, status: c.status };
    return { loading: false, url: c.url, contentType: c.contentType };
  });
  const startedRef = useRef(state.url || state.error);

  const start = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    setState({ loading: true });
    fetchReceiptBlob(receiptId).then((res) => {
      if (res.error) setState({ loading: false, error: true, status: res.status });
      else setState({ loading: false, url: res.url, contentType: res.contentType });
    });
  };

  // Kick off the fetch as soon as the popover content mounts.
  useEffect(() => {
    start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.loading || (!state.url && !state.error)) {
    return (
      <div className="flex h-32 items-center justify-center text-slate-400" data-testid="receipt-popover-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (state.error) {
    const msg = state.status === 404 ? "Receipt not found"
              : state.status === 403 ? "Access denied"
              : "Couldn't load receipt";
    return (
      <div className="flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700"
        data-testid="receipt-popover-error">
        <AlertTriangle className="h-4 w-4" /> {msg}
      </div>
    );
  }
  // Small provenance line shown in the receipt popover for Toll-ingested
  // receipts. `document_id` is truncated to 8 chars — full id is on the
  // expense record and in the audit log.
  const src = expense?.receipt_source;
  const tollTag = src && src.kind === "toll_app" && src.document_id
    ? (
      <p className="text-[11px] italic text-slate-500" data-testid="receipt-source-toll">
        From Toll · document {String(src.document_id).slice(0, 8)}
      </p>
    ) : null;

  // PDF tile (lessons learned from the design rule: no embedded PDF.js,
  // browser handles rendering when the user clicks Open PDF).
  if ((state.contentType || "").startsWith("application/pdf")) {
    const fname = expense?.receipt_filename || "receipt.pdf";
    const sizeKb = expense?.receipt_size_bytes ? (expense.receipt_size_bytes / 1024).toFixed(1) : null;
    const sizeLabel = sizeKb && sizeKb >= 1024 ? `${(sizeKb / 1024).toFixed(1)} MB`
                    : sizeKb ? `${sizeKb} KB` : "";
    return (
      <div className="space-y-2" data-testid="receipt-popover-pdf">
        <div className="flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
          <FileText className="h-10 w-10 shrink-0 text-rose-600" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-slate-900" title={fname}>{fname}</p>
            <p className="text-xs text-slate-500">PDF{sizeLabel ? ` · ${sizeLabel}` : ""}</p>
          </div>
        </div>
        <a href={state.url} target="_blank" rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs font-medium text-[#0F1E3D] hover:underline"
          data-testid="receipt-popover-open-pdf">
          <ExternalLink className="h-3 w-3" /> Open PDF
        </a>
        {tollTag}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenModal(state.url, state.contentType); }}
        className="block w-full overflow-hidden rounded-md border border-slate-200 bg-slate-50"
        data-testid="receipt-popover-thumb">
        <img
          src={state.url}
          alt="Receipt thumbnail"
          loading="lazy"
          className="block h-auto max-h-[260px] w-full object-contain"
        />
      </button>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpenModal(state.url, state.contentType); }}
        className="inline-flex items-center gap-1 text-xs font-medium text-[#0F1E3D] hover:underline"
        data-testid="receipt-popover-open-original">
        <ExternalLink className="h-3 w-3" /> Open original
      </button>
      {tollTag}
    </div>
  );
}

function ReceiptPopover({ expense, size, onOpenModal }) {
  const iconCls = size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5";
  const isPdf = expense?.receipt_content_type === "application/pdf";
  // Differentiated row icon for PDFs — red-tinted FileText so accounting can
  // see at a glance which expenses carry a PDF (vs the usual image).
  const IconComp = isPdf ? FileText : ReceiptIcon;
  const iconColor = isPdf
    ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700"
    : "text-slate-400 hover:bg-slate-100 hover:text-slate-700";
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
          aria-label={isPdf ? "Show PDF receipt" : "Show receipt"}
          data-testid={`expense-row-receipt-icon-${expense.id}`}
          className={`inline-flex h-5 w-5 items-center justify-center rounded ${iconColor}`}>
          <IconComp className={iconCls} />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="center"
        className="w-72 p-2"
        data-testid={`expense-row-receipt-popover-${expense.id}`}
        onClick={(e) => e.stopPropagation()}>
        <ReceiptThumb receiptId={expense.receipt_image_id || expense.receipt_file_id} expense={expense} onOpenModal={onOpenModal} />
      </HoverCardContent>
    </HoverCard>
  );
}

function ReceiptFullModal({ open, onOpenChange, url, contentType }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="receipt-full-modal"
        className="max-w-5xl border-none bg-transparent p-0 shadow-none">
        {url && (
          contentType === "application/pdf" ? (
            <iframe
              title="Receipt"
              src={url}
              className="h-[90vh] w-full rounded-md bg-white"
            />
          ) : (
            <img
              src={url}
              alt="Receipt"
              data-testid="receipt-full-modal-image"
              className="mx-auto max-h-[90vh] w-auto max-w-full rounded-md bg-white object-contain shadow-2xl"
            />
          )
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inline pair of indicator icons that reveal full content on hover (desktop)
 * or tap (mobile, via Radix HoverCard touch fallback). Reuse this component
 * everywhere we want the icons + previews: ExpenseList, DriverCharges,
 * StatementDetail's matched-expense cell, etc.
 *
 * Props:
 *  - `expense`: must include at minimum `id`. Reads `comment`,
 *    `comment_author_name`, `comment_at`, `user_name`, `receipt_image_id`.
 *  - `size`: "sm" for compact (mobile / matched-expense cells), default for
 *    desktop rows.
 */
export default function ExpenseRowIcons({ expense, size, className }) {
  const [modal, setModal] = useState({ open: false, url: null, contentType: null });
  const hasComment = !!(expense && expense.comment);
  const hasReceipt = !!(expense && (expense.receipt_image_id || expense.receipt_file_id));
  if (!hasComment && !hasReceipt) return null;
  const openModal = (url, contentType) => setModal({ open: true, url, contentType });
  const wrap = `inline-flex items-center gap-0.5 ${className || ""}`;
  return (
    <span className={wrap} data-testid={`expense-row-icons-${expense.id}`}>
      {hasComment && <CommentPopover expense={expense} size={size} />}
      {hasReceipt && <ReceiptPopover expense={expense} size={size} onOpenModal={openModal} />}
      <ReceiptFullModal
        open={modal.open}
        onOpenChange={(o) => setModal((m) => ({ ...m, open: o }))}
        url={modal.url}
        contentType={modal.contentType}
      />
    </span>
  );
}
